import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useStaffPush } from "../hooks/useStaffPush";
import { useAuth } from "../auth/AuthContext";
import HelpBadge from "./HelpBadge";

/**
 * The staff push opt-in, on the Health alerting page.
 *
 * WHY IT LIVES HERE AND NOT IN A GLOBAL MENU. The prompt has to come from an
 * explicit gesture (a denied notification permission is sticky per origin and
 * effectively un-askable afterwards), and it has to come with the context that
 * makes the answer meaningful: what gets pushed, and to whom. Health alerting
 * is where an operator is already deciding both.
 *
 * IT NAMES THE AUDIENCE RULE OUT LOUD. Subscribing is necessary but not
 * sufficient - a digest is pushed to the addresses on the rule's recipient list
 * (or the segment address book it inherits), matched to subscriptions by email.
 * Leaving that implicit is how somebody ends up trusting a channel that was
 * never going to reach them.
 */
export default function StaffPushOptIn() {
  const { user } = useAuth();
  const { state, busy, error, otherDevices, enable, disable } = useStaffPush();
  const { t } = useTranslation("offers");

  const email = user?.user ?? t("staffPushOptIn.yourAccount");

  return (
    <Card variant="outlined" sx={{ p: 2.5, position: "relative" }}>
      <HelpBadge title={t("staffPushOptIn.help")} />
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
            {t("staffPushOptIn.title")}
          </Typography>
          {state === "on" && (
            <Chip
              size="small"
              color="success"
              label={t("staffPushOptIn.state.on")}
            />
          )}
          {state === "off" && (
            <Chip
              size="small"
              variant="outlined"
              label={t("staffPushOptIn.state.off")}
            />
          )}
          {state === "denied" && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t("staffPushOptIn.state.denied")}
            />
          )}
        </Stack>

        {state === "loading" && (
          <Typography variant="body2" color="text.secondary">
            {t("staffPushOptIn.checking")}
          </Typography>
        )}

        {state === "unsupported" && (
          <Typography variant="body2" color="text.secondary">
            {t("staffPushOptIn.unsupported")}
          </Typography>
        )}

        {state === "unconfigured" && (
          <Alert severity="info" variant="outlined">
            <Trans
              t={t}
              i18nKey="staffPushOptIn.unconfigured"
              components={{ code: <code /> }}
            />
          </Alert>
        )}

        {state === "denied" && (
          <Alert severity="warning" variant="outlined">
            {t("staffPushOptIn.denied")}
          </Alert>
        )}

        {(state === "off" || state === "on") && (
          <Typography variant="body2" color="text.secondary">
            {state === "on"
              ? t("staffPushOptIn.onIntro")
              : t("staffPushOptIn.offIntro")}{" "}
            <Trans
              t={t}
              i18nKey="staffPushOptIn.audience"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        )}

        {otherDevices > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t("staffPushOptIn.otherDevices", { count: otherDevices })}
          </Typography>
        )}

        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}

        {(state === "off" || state === "on") && (
          <Box>
            {state === "off" ? (
              <Button
                variant="contained"
                size="small"
                disabled={busy}
                onClick={() => void enable()}
                data-testid="staff-push-enable"
              >
                {t("staffPushOptIn.enable")}
              </Button>
            ) : (
              <Button
                size="small"
                color="inherit"
                disabled={busy}
                onClick={() => void disable()}
                data-testid="staff-push-disable"
              >
                {t("staffPushOptIn.disable")}
              </Button>
            )}
          </Box>
        )}
      </Stack>
    </Card>
  );
}
